import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MonitorSmartphone, Pencil } from "lucide-react";

// Register authorization for one staff member (many-to-many). "All registers" = null on the server (default).
// Never changes staff seats (unique active users) or register seats (active terminals).
export function StaffRegisters({ u, registers, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [all, setAll] = useState(u.register_access !== "selected");
  const [picked, setPicked] = useState(new Set(u.register_ids || []));
  const active = (registers || []).filter((r) => r.status === "active");
  if (!active.length) return null;
  const locIds = new Set((u.locations || []).map((l) => l.id));
  const options = locIds.size ? active.filter((r) => locIds.has(r.location_id)) : active;
  const save = async () => {
    try {
      await api.put(`/users/${u.id}/registers`, { register_ids: all ? null : [...picked], reason: "updated by owner" });
      toast.success(all ? "Access to all registers" : `Authorized on ${picked.size} register${picked.size === 1 ? "" : "s"}`);
      setEditing(false); onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggle = (id) => { const n = new Set(picked); n.has(id) ? n.delete(id) : n.add(id); setPicked(n); };
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`staff-registers-${u.id}`}>
      <MonitorSmartphone className="w-3.5 h-3.5 text-muted-foreground" />
      {!editing && (
        <>
          {u.register_access !== "selected"
            ? <Badge variant="outline" className="text-[10px]" data-testid={`staff-reg-all-${u.id}`}>All registers</Badge>
            : (u.registers || []).length === 0
              ? <span className="text-[11px] text-amber-600" data-testid={`staff-reg-none-${u.id}`}>No register access</span>
              : (u.registers || []).map((r) => <Badge key={r.id} variant="outline" className="text-[10px]" data-testid={`staff-reg-chip-${u.id}-${r.id}`}>{r.name}</Badge>)}
          <button type="button" onClick={() => { setAll(u.register_access !== "selected"); setPicked(new Set(u.register_ids || [])); setEditing(true); }}
            className="text-[11px] text-emerald-600 hover:underline flex items-center gap-0.5" data-testid={`staff-reg-edit-${u.id}`}><Pencil className="w-3 h-3" /> registers</button>
        </>
      )}
      {editing && (
        <div className="w-full rounded-lg border border-border p-2 space-y-1.5 text-xs" data-testid={`staff-reg-editor-${u.id}`}>
          <label className="flex items-center gap-2 font-medium">
            <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} data-testid={`staff-reg-all-toggle-${u.id}`} /> All registers at assigned locations
          </label>
          {!all && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 pl-1">
              {options.map((r) => (
                <label key={r.id} className="flex items-center gap-2">
                  <input type="checkbox" checked={picked.has(r.id)} onChange={() => toggle(r.id)} data-testid={`staff-reg-opt-${u.id}-${r.id}`} />
                  {r.display_name} <span className="text-muted-foreground">· {r.location_name}</span>
                </label>
              ))}
              {options.length === 0 && <span className="text-amber-600">No active registers at this staff member's locations.</span>}
            </div>
          )}
          <div className="flex gap-1.5 pt-1">
            <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} data-testid={`staff-reg-save-${u.id}`}>Save</Button>
            <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
